<template>
  <div class="kwt-toolbar" v-if="kwtMode !== 'previewLite'">
    <div :class="['toolbar-row', { show: fullScreenVisible }]">
      <a @click.prevent="toggleFullscreen">
        <fullscreen-icon :is-fullscreen="isFullscreen" icon-name="Fullscreen Toggle">
        </fullscreen-icon>
      </a>
    </div>
    <div :class="['toolbar-row', { show: restartVisible }]">
      <a @click="restartActivity(true)">
        <restart-icon icon-name="Restart Activity"></restart-icon>
      </a>
    </div>
    <div v-if="kwtMode !== 'preview'">
      <div :class="['toolbar-row', { show: logoutVisible }]">
        <a :href="logoutUrlWithSession" aria-label="Log Out" class="pure-button" tabindex="4">
          <stop-icon icon-name="Stop"></stop-icon>
        </a>
      </div>
      <div :class="['toolbar-row', { show: videoVisible }]">
        <a @click="showVideo">
          <video-icon icon-name="Play Video"></video-icon>
        </a>
      </div>
      <div :class="['toolbar-row', { show: userProgressVisible }]">
        <a @click="showProgress">
          <progress-icon icon-name="Show Progress"></progress-icon>
        </a>
      </div>
      <div :class="['toolbar-row', { show: isCertVisible }]">
        <a @click="showCert">
          <certificate-icon icon-name="Show Certificate"></certificate-icon>
        </a>
      </div>
      <div :class="['toolbar-row', { show: usernameVisible }]">
        <p class="username">{{ username }}</p>
      </div>
      <div :class="['toolbar-row', 'activity-data', { show: activityDataVisible }]">
        <p>Week {{ nextWeek }}</p>
        <p>#{{ nextASN }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import screenfull from 'screenfull';
import FullscreenIcon from 'components/icons/FullscreenIcon.vue';
import RestartIcon from 'components/icons/RestartIcon.vue';
import StopIcon from 'components/icons/StopIcon.vue';
import ProgressIcon from 'components/icons/ProgressIcon.vue';
import VideoIcon from 'components/icons/VideoIcon.vue';
import CertificateIcon from 'components/icons/CertificateIcon.vue';

const activity = createNamespacedHelpers('activity');
const student = createNamespacedHelpers('student');
const application = createNamespacedHelpers('application');
const keyboard = createNamespacedHelpers('keyboard');

export default {
  components: {
    FullscreenIcon,
    RestartIcon,
    StopIcon,
    VideoIcon,
    ProgressIcon,
    CertificateIcon
  },

  computed: {
    ...activity.mapState({
      hasVideo: state => state.data.showVideo,
      playVideo: state => state.data.playVideo,
      isSpotCheck: state => Boolean(state.data.spotCheck)
    }),
    ...activity.mapGetters(['activityInProgress', 'templateVideo']),
    ...application.mapGetters(['logoutUrl']),
    ...student.mapState(['username', 'homeUseToken', 'token']),
    ...student.mapState({
      nextWeek: ({ position: { next: { week } = {} } = {} }) => week || 1,
      nextASN: ({ position: { next: { asn } = {} } = {} }) => asn || 1,
      isSpeedyKid: ({ position: { next: { theme: { id } = {} } = {} } = {} }) => id === 'SpeedyKid',
      lastPosition: state => state.position.last || {},
      nextPosition: state => state.position.next || {},
      sessionId: state => state.sessionId
    }),

    ...application.mapState(['device', 'kwtMode', 'toolbarMode']),

    isCertVisible() {
      const {
        lastPosition: {
          progress: { curriculum: { position, total } = {} } = {},
          theme: { id: prevThemeId } = {}
        },
        nextPosition: { theme: { id: themeId } = {} }
      } = this;

      return (
        position &&
        total &&
        position === total &&
        themeId === 'SpeedyKid' &&
        prevThemeId !== 'SpeedyKid'
      );
    },

    isMobile() {
      return this.device.isMobile;
    }
  },

  watch: {
    toolbarMode() {
      this.setToolbar(this.toolbarMode);
    },
    sessionId(newVal) {
      if (newVal) {
        this.logoutUrlWithSession = this.logoutUrl;
      }
    }
  },

  data() {
    if (screenfull.enabled) {
      screenfull.on('change', () => {
        this.isFullscreen = screenfull.isFullscreen;
      });
    }

    return {
      certVisible: false,
      isFullscreen: screenfull.enabled ? screenfull.isFullscreen : false,
      videoVisible: false,
      logoutVisible: false,
      userProgressVisible: false,
      usernameVisible: false,
      activityDataVisible: false,
      restartVisible: false,
      isSafari: false,
      fullScreenVisible: false,
      oldPlayVideo: null,
      logoutUrlWithSession: this.logoutUrl
    };
  },

  methods: {
    ...activity.mapActions(['restartActivity']),

    ...activity.mapMutations(['resetActivityStore', 'resetProgress', 'resetViewedIntro']),
    ...activity.mapMutations(['resetActivityStore', 'resetProgress', 'resetViewedIntro']),

    ...application.mapMutations(['setModal', 'resetApplication']),

    ...student.mapMutations(['resetStudent']),

    ...keyboard.mapMutations(['resetKeyboard']),

    setToolbar(mode) {
      switch (mode) {
        case 'inActivity':
          this.setInActivityToolbar();
          break;
        case 'award':
          this.setAwardToolbar();
          break;
        case 'inActivityPanel':
          this.setInActivityPanelToolbar();
          break;
        case 'fullScreenOnly':
          this.setFullScreenToolbar();
          break;
        default:
          this.hideAll();
      }
    },

    setFullScreenToolbar() {
      this.hideAll();
      this.isFullScreenOK();
    },

    setInActivityToolbar() {
      this.hideAll();
      this.fullScreenVisible = !this.isMobile;
      this.restartVisible = true;
    },

    setAwardToolbar() {
      this.hideAll();
      this.fullScreenVisible = !this.isMobile;
      this.userProgressVisible = true;
      this.setToolbarTransition();
      if (this.kwtMode === 'curriculum') {
        this.calculateWeekData();
      }
    },

    setInActivityPanelToolbar() {
      this.hideAll();
      this.fullScreenVisible = !this.isMobile;
      this.restartVisible = true;
      this.userProgressVisible = true;

      this.checkVideo();
      this.setToolbarTransition();
      if (this.kwtMode === 'curriculum') {
        this.calculateWeekData();
      }
    },

    hideAll() {
      this.userProgressVisible = false;
      this.logoutVisible = false;
      this.usernameVisible = false;
      this.restartVisible = false;
      this.activityDataVisible = false;
      this.videoVisible = false;
      this.fullScreenVisible = false;
      this.certVisible = false;
    },

    async toggleFullscreen() {
      if (screenfull.enabled) {
        screenfull.toggle();
      }
    },

    isFullScreenOK() {
      this.fullScreenVisible = !this.isMobile;
    },

    // Show Logout, Progress, and username
    setToolbarTransition() {
      this.isFullScreenOK();
      // Show logout only if user is NOT a token/clever user
      if (!this.homeUseToken && !this.token) {
        this.logoutVisible = true;
      }

      this.userProgressVisible = true;
      this.usernameVisible = true;
      this.restartVisible = false;

      if (this.isSpotCheck) {
        this.hideToolbar = true;
      } else {
        this.hideToolbar = false;
      }
    },

    checkVideo() {
      if (this.templateVideo !== undefined) {
        this.videoVisible = true;
      }
    },

    setToolbarAssessment() {
      this.hideAll();
      this.logoutVisible = true;
    },

    calculateWeekData() {
      if (this.kwtMode === 'curriculum' && !this.isSpeedyKid) {
        this.activityDataVisible = true;
      }
    },

    setToolbarRestart() {
      this.userProgressVisible = false;
      this.logoutVisible = false;
      this.usernameVisible = false;
      this.restartVisible = false;
    },

    showVideo() {
      if (this.templateVideo) {
        this.setModal({ type: 'video', data: this.templateVideo });
      }
    },

    showVideoFirstTime() {
      if (this.oldPlayVideo !== this.playVideo) {
        this.setModal({ type: 'video', data: this.playVideo });
        this.oldPlayVideo = this.playVideo;
      }
    },

    showProgress() {
      this.setModal({ type: 'progress', data: null });
    },

    showCert() {
      this.setModal({ type: 'cert', data: '' });
    }
  }
};
</script>

<style lang="scss" scoped>
div.kwt-toolbar {
  position: fixed;
  top: 5px;
  right: 5px;
  text-align: right;
  margin: 10px 8px;
  z-index: 1000;
  .toolbar-row {
    display: none;
    &.show {
      display: block;
    }
    &.activity-data {
      font-size: 11px;
      color: gray;
      text-align: right;
      p {
        white-space: nowrap;
        font-family: 'hwtfutura-semibold';
        margin: 0;
      }
    }
    a {
      cursor: pointer;
    }
    .username {
      text-align: right;
      font-size: 14px;
      font-family: 'hwtfutura-semibold';
      margin: 5px 3px 0 0;
      padding: 0;
      color: black;
    }
  }
}
</style>
